import { Star, Play, ArrowRight, ExternalLink } from "lucide-react";
import { testimonials } from "@/lib/data";
import { Link } from "react-router-dom";
import { StaggerGroup, StaggerItem, Reveal, GlowCard, Magnetic } from "@/components/Motion";
import { SITE } from "@/lib/api";
import SEO from "@/components/SEO";

export default function Testimonials() {
  return (
    <div data-testid="testimonials-page" className="relative overflow-hidden bg-[var(--color-bg)] min-h-screen">
      <SEO title="Patient Testimonials" description="Read stories and experiences from our patients." />

      <section className="py-16 lg:py-24 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="overline mb-4">Patient Reviews</div>
            <h1 className="font-heading text-5xl md:text-6xl text-[var(--color-text)] leading-[1.05] max-w-4xl">
              <em className="text-[var(--color-coral)] not-italic">4.8 / 5</em> from over 500 verified patients.
            </h1>
            <div className="mt-8 flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-[var(--color-coral)] text-[var(--color-coral)]" />
                ))}
              </div>
              <div className="text-sm text-[var(--color-text-muted)] font-semibold">Across Google, Practo &amp; Wellmed reviews</div>
              <a href={SITE.googleReviews} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[var(--color-coral)] font-semibold hover:gap-3 transition-all ml-auto md:ml-0">
                View Google Reviews <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <StaggerGroup className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar pb-8 md:pb-0 md:flex-wrap md:justify-center gap-6 relative" stagger={0.08}>
            {testimonials.map((t, i) => (
              <StaggerItem key={i} className="w-[85vw] shrink-0 snap-center md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] h-auto flex">
                <GlowCard data-testid={`testimonial-${i}`} className="p-7 w-full flex flex-col">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-[var(--color-coral)] text-[var(--color-coral)]" />
                    ))}
                  </div>
                  <p className="text-base text-white/80 font-heading italic leading-relaxed flex-1">&ldquo;{t.text}&rdquo;</p>
                  <div className="mt-5 pt-5 border-t border-[var(--color-border)] flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[var(--color-coral-light)] flex items-center justify-center text-[var(--color-coral)] font-heading text-lg">
                      {t.name[0]}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">{t.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[var(--color-coral)] font-semibold">Verified Patient</div>
                    </div>
                  </div>
                </GlowCard>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* YOUTUBE SLIDER PLACEHOLDER */}
      <section className="py-24 bg-[var(--color-surface)] border-y border-[var(--color-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl md:text-4xl text-[var(--color-text)] mb-4">Patient Journeys & Education</h2>
            <p className="text-[var(--color-text-muted)] max-w-2xl mx-auto mb-12">Watch our latest videos discussing women's health, pregnancy tips, and patient stories.</p>
          </Reveal>

          <div className="max-w-4xl mx-auto mb-10">
            <StaggerGroup className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar pb-6 md:pb-0 md:grid md:grid-cols-2 gap-6 md:gap-8" stagger={0.1}>
              {[
                "Da7NcnOx0Ds",
                "ocyKxOQRa70",
                "hqm6SE57opw",
                "6F9Pw7dtrtM"
              ].map((id) => (
                <StaggerItem key={id} className="w-[85vw] shrink-0 snap-center md:w-auto h-auto flex flex-col">
                  <div className="aspect-video w-full bg-[var(--color-bg-alt)] rounded-2xl border border-[var(--color-border)] overflow-hidden hover:border-[var(--color-coral)] transition-all shadow-sm">
                    <iframe 
                      width="100%" 
                      height="100%" 
                      src={`https://www.youtube.com/embed/${id}`} 
                      title="Patient Testimonial" 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen>
                    </iframe>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>
          </div>

          <Reveal delay={0.2}>
            <a href={SITE.youtube} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[var(--color-coral)] font-semibold hover:gap-3 transition-all">
              Subscribe to our YouTube Channel <ArrowRight className="w-4 h-4" />
            </a>
          </Reveal>
        </div>
      </section>

      <section className="py-24 relative">
        <Reveal>
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="glass-card rounded-3xl p-10 lg:p-14 text-center border border-[var(--color-border-2)]">
              <h2 className="font-heading text-3xl md:text-4xl text-white">Ready to write your own story?</h2>
              <div className="mt-6 flex flex-wrap justify-center gap-4">
                <Magnetic><Link to="/appointment" data-testid="testimonials-cta-book" className="btn-primary">Book Appointment <ArrowRight className="w-4 h-4" /></Link></Magnetic>
                <Magnetic><Link to="/contact" data-testid="testimonials-cta-contact" className="btn-outline">Contact Us</Link></Magnetic>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
